import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import _ from 'lodash';
import { useIntl } from 'react-intl';
import { withBreakpoints } from 'react-breakpoints';
import { useLocale, getModelByOutputChannel } from 'src/modules/locale';
import { LayoutBlockHero, LayoutCardSize, LayoutPage } from 'src/modules/layout';
import { PagesStore } from 'src/modules/pages';
import { Media } from 'react-breakpoints';
import { getImageBreakpointUrl } from 'src/utils/image';

import {
  ArticleLinkPrefix,
  ArticleListingOverview,
  getArticleCategory,
} from 'src/modules/articles';
import { GlobalBanner } from 'src/modules/global';
import { ArticlesListingStore } from '../../store/types';
import { Tabs } from 'antd';

import styles from './ArticlesListingPage.module.scss';
import { useRouter } from 'next/router';
import { breakpoints } from 'src/utils/breakpoints';

interface MagazinePage extends PagesStore, ArticlesListingStore {}
interface ArticleListingPageProps {
  magazinePage: MagazinePage;
  currentBreakpoint: string;
}

export const ArticleListingPage: React.FunctionComponent<ArticleListingPageProps> = (props) => {
  const intl = useIntl();
  const { getLocalizedObject, outputChannel } = useLocale();

  // This is used to force a redraw after the page is mounted.
  const [hack, setHack] = useState('');
  useEffect(() => {
    setHack('mounted');
  }, []);

  const pageProperties = {
    title: intl.formatMessage({ id: 'page/magazine/listing/head/page_title' }),
    description: intl.formatMessage({ id: 'page/magazine/listing/head/meta_description' }),
    breadcrumbs: [
      {
        title: intl.formatMessage({ id: 'breadcrumbs/magazine' }),
        as: '/magazine',
        href: '/magazine',
      },
    ],
  };

  const { currentBreakpoint } = props;

  const {
    article: firstArticle,
    articles = [],
    articleCategories = [],
    banner_top,
    banner_bottom,
  } = getLocalizedObject(props?.magazinePage);

  const bannerTopModel = getModelByOutputChannel(banner_top, outputChannel);
  const bannerBottomModel = getModelByOutputChannel(banner_bottom, outputChannel);

  const firstArticleWithResponsiveImages = { ...firstArticle };
  if (firstArticleWithResponsiveImages?.hero?.image?.url) {
    firstArticleWithResponsiveImages.hero.image.url = getImageBreakpointUrl(
      currentBreakpoint,
      firstArticle.hero?.image
    )?.url;
  }

  const articlesWithResponsiveImages = _.map(articles, (article) => ({
    ...article,
    hero: {
      ...article.hero,
      image: {
        ...article.hero?.image,
        url: getImageBreakpointUrl(currentBreakpoint, article.hero?.image)?.url,
      },
    },
  }));

  const [filteredArticles, setFilteredArticles] = useState(articlesWithResponsiveImages);
  const queryApplied = useRef(false);

  const { TabPane } = Tabs;

  const allKeyFilter = useMemo(() => {
    return intl.formatMessage({ id: 'page/magazine/listing/all' });
  }, [intl]);

  const allArticleCategories = useMemo(() => {
    return [{ name: allKeyFilter }, ...articleCategories];
  }, [allKeyFilter, articleCategories]);

  const router = useRouter();

  const activeTab = useMemo(() => {
    // if the index is not found, then we return 0 (-1 + 1) which is the "all" tab
    // if the index is found, we return the found index + 1 (which accounts for the "all" tab being first)
    return (articleCategories.findIndex((ac) => ac.name === router.query.category) + 1).toString();
  }, [articleCategories, router.query.category]);

  const isAllArticles = useCallback((key) => {
    return key === '0';
  }, []);

  // Query changed
  useEffect(() => {
    if (queryApplied.current) return;

    const categoryQuery = router.query?.category;
    if (!categoryQuery) return;

    queryApplied.current = true;
    setFilteredArticles(
      _.filter(articlesWithResponsiveImages, (article) => {
        const name = article?.article_category?.name;
        return categoryQuery === name;
      })
    );
  }, [router.query, articlesWithResponsiveImages]);

  const onTabChange = useCallback(
    (key) => {
      const nkey = parseInt(key, 10);
      const categoryName = articleCategories[nkey - 1]?.name || 'All';

      // Filtered articles
      if (categoryName !== 'All') {
        router.push(`/magazine?category=${encodeURIComponent(categoryName)}`, undefined, {
          shallow: true,
        });
        setFilteredArticles(
          _.filter(articlesWithResponsiveImages, (article) => {
            const name = article?.article_category?.name;
            return categoryName === name;
          })
        );
      }
      // All articles
      else {
        router.push(`/magazine`, undefined, { shallow: true });
        setFilteredArticles(articlesWithResponsiveImages);
      }

      queryApplied.current = true;
    },

    [articleCategories, router, articlesWithResponsiveImages]
  );

  const heroLink = `/magazine/${firstArticleWithResponsiveImages?.slug}`;
  return (
    <LayoutPage {...pageProperties} key={`articles-listing-page-${hack}`}>
      {firstArticleWithResponsiveImages && (
        <LayoutBlockHero
          {...firstArticleWithResponsiveImages.hero}
          label={getArticleCategory(firstArticleWithResponsiveImages)}
          link={heroLink}
        />
      )}
      <div className={styles.tabContainer}>
        <Tabs activeKey={activeTab.toString()} onChange={onTabChange}>
          {allArticleCategories.map((cat, idx) => (
            <TabPane
              tab={
                <div className={styles.tabWrapper}>
                  {cat.name}
                  {idx !== allArticleCategories.length - 1 && <div className={styles.separator} />}
                </div>
              }
              key={idx.toString()}></TabPane>
          ))}
        </Tabs>
        <div className={styles.count}>{`${_.size(filteredArticles)} ${intl.formatMessage({
          id: 'page/magazine/listing/articles',
        })}`}</div>
      </div>
      {isAllArticles(activeTab) ? (
        <div>
          <Media>
            {({ currentBreakpoint } = { currentBreakpoint: 'xs' }) => {
              return (
                <div key={`article-list-${currentBreakpoint}-${hack}`}>
                  <ArticleListingOverview
                    articles={filteredArticles.slice(0, 4)}
                    size={
                      breakpoints[currentBreakpoint] < breakpoints.md
                        ? LayoutCardSize.Small
                        : LayoutCardSize.Big
                    }
                    link={ArticleLinkPrefix.article}
                    relatedOverview={false}
                    oneRow={false}
                  />
                  {!!bannerTopModel && (
                    <div className={styles.bannerTop}>
                      <GlobalBanner {...bannerTopModel} />
                    </div>
                  )}
                  <ArticleListingOverview
                    articles={filteredArticles.slice(4)}
                    size={LayoutCardSize.Small}
                    link={ArticleLinkPrefix.article}
                    relatedOverview={false}
                    oneRow={false}
                  />
                </div>
              );
            }}
          </Media>
          {!!bannerBottomModel && (
            <div className={styles.bannerBottom}>
              <GlobalBanner {...bannerBottomModel} />
            </div>
          )}
        </div>
      ) : (
        <div>
          <ArticleListingOverview
            articles={filteredArticles}
            size={LayoutCardSize.Small}
            link={ArticleLinkPrefix.article}
            relatedOverview={false}
            oneRow={false}
          />
        </div>
      )}
    </LayoutPage>
  );
};

export default withBreakpoints(ArticleListingPage);
